import { useState } from 'react';
import { Grid2 as Grid, Button, Accordion, AccordionSummary, AccordionDetails, Typography, Tooltip, IconButton } from '@mui/material';
import { ExpandMore, Delete, DragHandle, InfoOutlined } from '@mui/icons-material';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import CommonCard from '../components/common_card';
import { TypographyBold } from '../style/typography';
import { CustomControlledInput } from "./CustomControlledInput";

const RecipientList = ({
    recipients,
    sendCategories,
    control,
    expandedRecipient,
    handleEditClick,
    handleCancelEdit,
    handleDeleteClick,
    handleEditSubmit,
    openDialog,
    title,
    droppableId,
    isDisabled
}) => {
    const [tooltipOpen, setTooltipOpen] = useState(false);

    return (
        <Grid size={{ xs: 12 }}>
            <CommonCard
                child={
                    <Grid container spacing={3} sx={{ padding: 3 }}>
                        <Grid size={{ xs: 12 }}>
                            <TypographyBold sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Tooltip
                                    title="＝をドラッグ&ドロップで並べ替えができます"
                                    arrow
                                    open={tooltipOpen}
                                    onClose={() => setTooltipOpen(false)}
                                    PopperProps={{
                                        placement: 'bottom-end'
                                    }}
                                >
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setTooltipOpen(prev => !prev);
                                        }}
                                    >
                                        <InfoOutlined fontSize="medium" sx={{ cursor: "pointer", color: "#0A7CB7" }} />
                                    </IconButton>
                                </Tooltip>
                                {title}
                            </TypographyBold>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Droppable droppableId={droppableId}>
                                {(provided) => (
                                    <div {...provided.droppableProps} ref={provided.innerRef}>
                                        {Array.isArray(recipients) && recipients.map((recipient, index) => (
                                            <Draggable key={recipient.uuid} draggableId={String(recipient.uuid)} index={index}>
                                                {(provided, snapshot) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        style={{ ...provided.draggableProps.style, marginBottom: '8px' }}
                                                    >
                                                        <Accordion
                                                            expanded={expandedRecipient === recipient.uuid}
                                                            onChange={() => {
                                                                if (expandedRecipient === recipient.uuid) {
                                                                    handleCancelEdit();
                                                                } else {
                                                                    handleEditClick(recipient);
                                                                }
                                                            }}
                                                            sx={{ backgroundColor: snapshot.isDragging ? '#f0f0f0' : 'inherit' }}
                                                        >
                                                            <AccordionSummary
                                                                expandIcon={<ExpandMore />}
                                                                aria-controls={`recipient-content-${recipient.uuid}`}
                                                                id={`recipient-header-${recipient.uuid}`}
                                                                sx={{ display: 'flex', alignItems: 'center' }}
                                                            >
                                                                <div {...provided.dragHandleProps} style={{ marginRight: '8px', cursor: 'grab', display: 'flex', alignItems: 'center' }}>
                                                                    <DragHandle color="action" />
                                                                </div>
                                                                <div>
                                                                    <Typography variant="h6" component="div">{recipient.name}</Typography>
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        {recipient.department_name} - {recipient.email}
                                                                    </Typography>
                                                                </div>
                                                            </AccordionSummary>
                                                            <AccordionDetails>
                                                                <Grid container spacing={2} sx={{ paddingTop: 3 }}>
                                                                    {/* 氏名 */}
                                                                    <Grid size={{ xs: 12, md: 6 }}>
                                                                        <CustomControlledInput
                                                                            name="editRecipient.name"
                                                                            control={control}
                                                                            label="氏名"
                                                                            variant="outlined"
                                                                            disabled={isDisabled}
                                                                        />
                                                                    </Grid>
                                                                    {/* 部署名, 肩書 */}
                                                                    <Grid size={{ xs: 12, md: 6 }}>
                                                                        <CustomControlledInput
                                                                            name="editRecipient.department_name"
                                                                            control={control}
                                                                            label="部署名, 肩書"
                                                                            variant="outlined"
                                                                            disabled={isDisabled}
                                                                        />
                                                                    </Grid>
                                                                    {/* メールアドレス */}
                                                                    <Grid size={{ xs: 12, md: 6 }}>
                                                                        <CustomControlledInput
                                                                            name="editRecipient.email"
                                                                            control={control}
                                                                            label="メールアドレス"
                                                                            variant="outlined"
                                                                            disabled={isDisabled}
                                                                        />
                                                                    </Grid>
                                                                    {/* 送付先カテゴリ */}
                                                                    <Grid size={{ xs: 12, md: 6 }}>
                                                                        <CustomControlledInput
                                                                            name="editRecipient.send_category"
                                                                            control={control}
                                                                            label="送付先カテゴリ"
                                                                            type="select"
                                                                            multiple={false}
                                                                            options={sendCategories.map((c) => ({
                                                                                value: c.value,
                                                                                label: c.label
                                                                            }))}
                                                                            disabled={true}
                                                                        />
                                                                    </Grid>
                                                                    {/* ボタン群 */}
                                                                    <Grid
                                                                        size={{ xs: 12 }}
                                                                        display="flex"
                                                                        justifyContent="space-between"
                                                                        gap={2}
                                                                        sx={{ mt: 2 }}
                                                                    >
                                                                        <Button variant="outlined" color="error" onClick={() => handleDeleteClick(recipient)} startIcon={<Delete />} disabled={isDisabled}>削除</Button>
                                                                        <div style={{ display: 'flex', gap: '8px' }}>
                                                                            <Button variant="outlined" onClick={handleCancelEdit} disabled={isDisabled}>
                                                                                キャンセル
                                                                            </Button>
                                                                            <Button
                                                                                variant="contained"
                                                                                onClick={handleEditSubmit(
                                                                                    (data) => { openDialog('save', data.editRecipient); }
                                                                                )}
                                                                                sx={{
                                                                                    backgroundColor: 'rgba(124, 183, 10, 1.0)',
                                                                                    '&:hover': { backgroundColor: 'rgba(124, 183, 10, 0.8)' }
                                                                                }}
                                                                                disabled={isDisabled}
                                                                            >
                                                                                保存
                                                                            </Button>
                                                                        </div>
                                                                    </Grid>
                                                                </Grid>
                                                            </AccordionDetails>
                                                        </Accordion>
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </Grid>
                    </Grid>
                }
            />
        </Grid>
    );
};

export default RecipientList;